import { useRef, useState } from "react";
import { Icons } from "assets/svgs";
import { useOutsideClick } from "hooks";

export const FilterSelect = ({
  buttonLabel,
  options = [],
  selectedOptions = [],
  setSelectedOptions,
  className = "",
  showSelectClear = true,
  align = "left",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedCount = selectedOptions.length;
  const isActive = selectedCount > 0;
  const allSelected = options.length > 0 && selectedCount === options.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleOption = (value) => {
    const index = selectedOptions.indexOf(value);
    if (index === -1) {
      setSelectedOptions([...selectedOptions, value]);
    } else {
      const next = [...selectedOptions];
      next.splice(index, 1);
      setSelectedOptions(next);
    }
  };

  const selectAll = () => setSelectedOptions(options.map((o) => o.value));
  const clearAll = () => setSelectedOptions([]);

  useOutsideClick((e) => {
    if (ref.current && !ref.current.contains(e.target)) setOpen(false);
  });

  return (
    <div ref={ref} className={`relative h-10 ${className}`}>
      <button
        className={`flex h-10 min-w-[180px] items-center justify-between gap-2 rounded-lg border px-3.5 text-[13px] font-semibold uppercase tracking-[0.03em] outline-none transition-colors ${
          isActive || open
            ? "border-[#437f55] bg-[#e8f5ee] text-[#055a17] ring-2 ring-[#437f55]/15"
            : "border-[#dddddd] bg-white text-[#333] hover:border-[#808080]"
        }`}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {buttonLabel}
          {isActive ? (
            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#0B9159] px-1 text-[10px] font-bold leading-none text-white">
              {selectedCount}
            </span>
          ) : null}
        </span>
        <Icons.ChevronRightIcon
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "rotate-90" : "rotate-0"}`}
        />
      </button>

      {open ? (
        <div
          className={`absolute top-[46px] z-20 min-w-[210px] origin-top animate-second-animation rounded-xl border border-[#e2e8f0] bg-white p-2 shadow-[0_12px_30px_-10px_rgba(16,24,40,0.25)] ${
            align === "right" ? "right-0" : "left-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {showSelectClear ? (
            <div className="mb-2 rounded-lg bg-[#f1f5f9] p-1">
              <button
                aria-label="Select all"
                className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-[12px] font-semibold text-[#333] transition-colors hover:bg-[#e2e8f0]"
                type="button"
                onClick={allSelected ? clearAll : selectAll}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                    allSelected || someSelected ? "border-[#0B9159] bg-[#0B9159]" : "border-[#cbd5e1] bg-white"
                  }`}
                >
                  {allSelected ? (
                    <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                      <path
                        d="M2.5 6.2 4.8 8.5 9.5 3.8"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                      />
                    </svg>
                  ) : someSelected ? (
                    <span className="h-[2px] w-2 rounded-full bg-white" />
                  ) : null}
                </span>
                Select all
              </button>
            </div>
          ) : null}

          <div className="flex flex-col gap-0.5">
            {options.map((item) => {
              const checked = selectedOptions.includes(item.value);
              return (
                <button
                  key={item.value}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                    checked ? "bg-[#e8f5ee] font-semibold text-[#055a17]" : "text-[#333] hover:bg-[#f8fafc]"
                  }`}
                  type="button"
                  onClick={() => toggleOption(item.value)}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                      checked ? "border-[#0B9159] bg-[#0B9159]" : "border-[#cbd5e1] bg-white"
                    }`}
                  >
                    {checked ? (
                      <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                        <path
                          d="M2.5 6.2 4.8 8.5 9.5 3.8"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.8"
                        />
                      </svg>
                    ) : null}
                  </span>
                  <span className="whitespace-nowrap">{item.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
